import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Table, Modal, InputNumber } from 'antd';
import { toast, Bounce } from 'react-toastify';
import { Button } from 'components/Button';
import { getStockTakeDetailApi, updateStockTakeApi, completeStockTakeApi } from 'api/services/uniformManagement';
import { formatDateHandler } from 'utils/common';
import { getStatusBadge } from '../../constants';

const StockTakeDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [stockTake, setStockTake] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [editedItems, setEditedItems] = useState({});

  const fetchData = () => {
    setLoading(true);
    getStockTakeDetailApi(id).then(response => {
      setStockTake(response.data?.data || null);
      setEditedItems({});
      setLoading(false);
    }).catch(() => { setLoading(false); navigate('/uniform-management/stock-take'); });
  };

  useEffect(() => { fetchData(); }, [id]);

  const handlePhysicalQtyChange = (itemId, value) => {
    setEditedItems(prev => ({ ...prev, [itemId]: value }));
  };

  const getPhysicalQty = (item) => {
    return editedItems[item.id] !== undefined ? editedItems[item.id] : item.physical_quantity;
  };

  const getVariance = (item) => {
    const physical = getPhysicalQty(item);
    return physical - item.system_quantity;
  };

  const handleSave = () => {
    const items = Object.entries(editedItems).map(([itemId, qty]) => ({
      id: parseInt(itemId),
      physical_quantity: qty
    }));
    if (items.length === 0) {
      toast.info('No changes to save', { position: "top-right", autoClose: 3000, transition: Bounce });
      return;
    }
    setSaving(true);
    updateStockTakeApi(id, { items }).then(response => {
      setStockTake(response.data?.data || stockTake);
      setEditedItems({});
      setSaving(false);
      toast.success('Counts saved', { position: "top-right", autoClose: 3000, transition: Bounce });
    }).catch(() => {
      setSaving(false);
      toast.error('Failed to save counts', { position: "top-right", autoClose: 3000, transition: Bounce });
    });
  };

  const handleComplete = () => {
    Modal.confirm({
      title: 'Complete Stock Take',
      content: 'This will adjust inventory to match physical counts. This action is irreversible. Continue?',
      okText: 'Complete',
      okType: 'danger',
      onOk: () => {
        setCompleting(true);
        completeStockTakeApi(id).then(() => {
          toast.success('Stock take completed, inventory adjusted', { position: "top-right", autoClose: 3000, transition: Bounce });
          setCompleting(false);
          fetchData();
        }).catch(() => {
          setCompleting(false);
          toast.error('Failed to complete stock take', { position: "top-right", autoClose: 3000, transition: Bounce });
        });
      }
    });
  };

  if (loading || !stockTake) {
    return (
      <div className="page-wrapper">
        <div className="content container-fluid">
          <div className="text-center py-5"><i className="fa fa-spinner fa-spin fa-2x" /></div>
        </div>
      </div>
    );
  }

  const isInProgress = stockTake.status === 'IN_PROGRESS';
  const hasChanges = Object.keys(editedItems).length > 0;

  const columns = [
    { title: 'Uniform Item', dataIndex: 'uniform_item_name' },
    { title: 'Size', dataIndex: 'size_code' },
    { title: 'System Qty', dataIndex: 'system_quantity' },
    {
      title: 'Physical Qty',
      render: (_, record) => isInProgress ? (
        <InputNumber
          min={0}
          value={getPhysicalQty(record)}
          onChange={(val) => handlePhysicalQtyChange(record.id, val)}
          style={{ width: 80 }}
        />
      ) : record.physical_quantity
    },
    {
      title: 'Variance',
      render: (_, record) => {
        const variance = isInProgress ? getVariance(record) : record.variance;
        return (
          <span style={{
            color: variance < 0 ? '#ff4d4f' : variance > 0 ? '#52c41a' : 'inherit',
            fontWeight: variance !== 0 ? 600 : 400
          }}>
            {variance > 0 ? `+${variance}` : variance}
          </span>
        );
      }
    }
  ];

  return (
    <div className="page-wrapper">
      <div className="content container-fluid">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
<h3 className="d-inline">Stock Take - {stockTake.warehouse_name}</h3>
          </div>
          <span style={getStatusBadge(stockTake.status)}>
            {stockTake.status.replace('_', ' ')}
          </span>
        </div>

        <div className="card mb-3">
          <div className="card-body">
            <div className="row">
              <div className="col-md-3 mb-2"><strong>Conducted By:</strong> {stockTake.conducted_by_name}</div>
              <div className="col-md-3 mb-2"><strong>Date:</strong> {formatDateHandler(stockTake.conducted_date)}</div>
              <div className="col-md-3 mb-2"><strong>Items:</strong> {stockTake.item_count}</div>
              <div className="col-md-3 mb-2">
                <strong>Total Variance:</strong>{' '}
                <span style={{ color: stockTake.total_variance < 0 ? '#ff4d4f' : stockTake.total_variance > 0 ? '#52c41a' : 'inherit', fontWeight: 600 }}>
                  {stockTake.total_variance > 0 ? `+${stockTake.total_variance}` : stockTake.total_variance}
                </span>
              </div>
              {stockTake.notes && (
                <div className="col-md-12 mb-2"><strong>Notes:</strong> {stockTake.notes}</div>
              )}
            </div>
          </div>
        </div>

        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5 className="mb-0">Count Items</h5>
            {isInProgress && (
              <div className="d-flex gap-2">
                <Button label="Save Counts" icon="fa fa-save" className="btn btn-primary btn-sm"
                  loading={saving} disabled={!hasChanges || saving} onClick={handleSave} />
                <Button label="Complete Stock Take" icon="fa fa-check" className="btn btn-success btn-sm"
                  loading={completing} disabled={completing} onClick={handleComplete} />
              </div>
            )}
          </div>
          <div className="card-body">
            <Table columns={columns} dataSource={stockTake.items || []} rowKey={(r) => r.id}
              pagination={false} scroll={{ x: "max-content" }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default StockTakeDetail;
